import React, { useEffect, useState, Suspense, lazy, memo, Component } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
// Install array interceptor FIRST
import { installArrayInterceptor } from './utils/safeArray';
installArrayInterceptor();
// Global error handler to catch all runtime errors
if (typeof window !== 'undefined') {
  window.onerror = (msg, src, line, col, err) => {
    console.error('====================================');
    console.error('[GLOBAL ERROR] Uncaught runtime error!');
    console.error('[GLOBAL ERROR] Message:', msg);
    console.error('[GLOBAL ERROR] Source:', src);
    console.error('[GLOBAL ERROR] Line:', line, 'Column:', col);
    console.error('[GLOBAL ERROR] Error object:', err);
    console.error('[GLOBAL ERROR] Stack:', err?.stack);
    console.error('====================================');
    return false;
  };
  window.addEventListener('unhandledrejection', event => {
    console.error('====================================');
    console.error('[GLOBAL ERROR] Unhandled promise rejection!');
    console.error('[GLOBAL ERROR] Reason:', event.reason);
    console.error('[GLOBAL ERROR] Promise:', event.promise);
    console.error('====================================');
  });
  (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__ = {
    ...((window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__ || {}),
    supportsFiber: true
  };
}
// Context Providers
import { ThemeProvider } from './components/ThemeProvider';
import { AuthProvider } from './contexts/AuthContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { ToastProvider } from './contexts/ToastProvider';
// Components
import { ProtectedRoute } from './components/ProtectedRoute';
import { Navigation } from './components/Navigation';
import { TopBar } from './components/TopBar';
import { Spinner } from './components/Spinner';
import { PasswordGate } from './components/PasswordGate';
import { KeyboardShortcutsModal } from './components/KeyboardShortcutsModal';
import { ChatbotWidget } from './components/ChatbotWidget';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ScrollToTop } from './components/ScrollToTop';
import { Footer } from './components/Footer';
// Hooks
import { useGlobalKeyboardShortcuts, useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useOrganization } from './contexts/OrganizationContext';
// Lazy loaded pages for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({
  default: m.Dashboard
})));
const OrganizationSetup = lazy(() => import('./pages/OrganizationSetup').then(m => ({
  default: m.OrganizationSetup
})));
const Taxonomy = lazy(() => import('./pages/Taxonomy').then(m => ({
  default: m.Taxonomy
})));
const ImageLibrary = lazy(() => import('./pages/ImageLibrary').then(m => ({
  default: m.ImageLibrary
})));
const ImageSets = lazy(() => import('./pages/ImageSets').then(m => ({
  default: m.ImageSets
})));
const Supersets = lazy(() => import('./pages/Supersets').then(m => ({
  default: m.Supersets
})));
const ExerciseAuthoring = lazy(() => import('./pages/ExerciseAuthoring').then(m => ({
  default: m.ExerciseAuthoring
})));
const PublishExercise = lazy(() => import('./pages/PublishExercise').then(m => ({
  default: m.PublishExercise
})));
const RunnerPreview = lazy(() => import('./pages/RunnerPreview').then(m => ({
  default: m.RunnerPreview
})));
const Results = lazy(() => import('./pages/Results').then(m => ({
  default: m.Results
})));
const Integrations = lazy(() => import('./pages/Integrations').then(m => ({
  default: m.Integrations
})));
const Analytics = lazy(() => import('./pages/Analytics').then(m => ({
  default: m.Analytics
})));
const RolesAndAccess = lazy(() => import('./pages/RolesAndAccess').then(m => ({
  default: m.RolesAndAccess
})));
const ManageExercises = lazy(() => import('./pages/ManageExercises').then(m => ({
  default: m.ManageExercises
})));
const ParticipantManagement = lazy(() => import('./pages/ParticipantManagement').then(m => ({
  default: m.ParticipantManagement
})));
const AssignmentManagement = lazy(() => import('./pages/AssignmentManagement').then(m => ({
  default: m.AssignmentManagement
})));
const VerificationTest = lazy(() => import('./pages/VerificationTest').then(m => ({
  default: m.VerificationTest
})));
function ChatbotWrapper() {
  const location = useLocation();
  const isResultsPage = location.pathname === '/results';
  return <ChatbotWidget assessmentContext={isResultsPage ? {
    type: 'individual',
    data: null
  } : undefined} data-id="element-0" />;
}
const MemoizedNavigation = memo(Navigation);
const MemoizedTopBar = memo(TopBar);
const MemoizedFooter = memo(Footer);
function AppContent() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const globalShortcuts = useGlobalKeyboardShortcuts() || [];
  const orgContext = useOrganization();
  useEffect(() => {
    const authFlag = localStorage.getItem('demo-authenticated');
    setIsAuthenticated(authFlag === 'true');
    setIsChecking(false);
  }, []);
  const shortcutsArray = [{
    key: '?',
    shiftKey: true,
    action: () => setShowKeyboardShortcuts(true)
  }, {
    key: 'Escape',
    action: () => setShowKeyboardShortcuts(false)
  }, ...(Array.isArray(globalShortcuts) ? globalShortcuts : [])];
  useKeyboardShortcuts(shortcutsArray, isAuthenticated);
  if (isChecking) {
    return <div className="min-h-screen w-full flex items-center justify-center bg-gray-50" data-id="element-1">
        <Spinner data-id="element-2" />
      </div>;
  }
  if (!isAuthenticated) {
    return <PasswordGate onSuccess={() => {
      setIsAuthenticated(true);
    }} data-id="element-3" />;
  }
  return <div className="app-shell h-screen flex bg-gray-50" data-id="element-4">
      <ErrorBoundary data-id="element-5">
        <MemoizedNavigation data-id="element-6" />
      </ErrorBoundary>
      <div className="flex-1 ml-64 flex flex-col h-screen overflow-hidden" data-id="element-7">
        <ErrorBoundary data-id="element-8">
          <MemoizedTopBar data-id="element-9" />
        </ErrorBoundary>
        <main className="flex-1 overflow-y-auto bg-gray-50 custom-scrollbar" id="main-content" data-id="element-10">
          <div className="min-h-full flex flex-col" data-id="element-11">
            <div className="flex-1" data-id="element-12">
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center" data-id="element-14">
                    <Spinner data-id="element-15" />
                  </div>} data-id="element-13">
                <ErrorBoundary data-id="element-16">
                  <Routes data-id="element-17">
                    <Route path="/" element={<Navigate to="/dashboard" replace data-id="element-19" />} data-id="element-18" />
                    <Route path="/dashboard" element={<Dashboard data-id="element-21" />} data-id="element-20" />
                    <Route path="/organization-setup" element={<ProtectedRoute allowed={['agilebrain-admin']} data-id="element-23">
                          <OrganizationSetup data-id="element-24" />
                        </ProtectedRoute>} data-id="element-22" />
                    <Route path="/roles-and-access" element={<ProtectedRoute allowed={['agilebrain-admin']} data-id="element-26">
                          <RolesAndAccess data-id="element-27" />
                        </ProtectedRoute>} data-id="element-25" />
                    <Route path="/participant-management" element={<ProtectedRoute allowed={['agilebrain-admin', 'client-admin']} data-id="element-29">
                          <ParticipantManagement data-id="element-30" />
                        </ProtectedRoute>} data-id="element-28" />
                    <Route path="/assignment-management" element={<ProtectedRoute allowed={['agilebrain-admin', 'client-admin']} data-id="element-32">
                          <AssignmentManagement data-id="element-33" />
                        </ProtectedRoute>} data-id="element-31" />
                    <Route path="/taxonomy" element={<Taxonomy data-id="element-35" />} data-id="element-34" />
                    <Route path="/image-library" element={<ProtectedRoute allowed={['agilebrain-admin']} data-id="element-37">
                          <ImageLibrary data-id="element-38" />
                        </ProtectedRoute>} data-id="element-36" />
                    <Route path="/image-sets" element={<ProtectedRoute allowed={['agilebrain-admin']} data-id="element-40">
                          <ImageSets data-id="element-41" />
                        </ProtectedRoute>} data-id="element-39" />
                    <Route path="/supersets" element={<ProtectedRoute allowed={['agilebrain-admin']} data-id="element-43">
                          <Supersets data-id="element-44" />
                        </ProtectedRoute>} data-id="element-42" />
                    <Route path="/exercise-authoring" element={<ProtectedRoute allowed={['agilebrain-admin']} data-id="element-46">
                          <ExerciseAuthoring data-id="element-47" />
                        </ProtectedRoute>} data-id="element-45" />
                    <Route path="/publish" element={<ProtectedRoute allowed={['agilebrain-admin']} data-id="element-49">
                          <PublishExercise data-id="element-50" />
                        </ProtectedRoute>} data-id="element-48" />
                    <Route path="/manage-exercises" element={<ProtectedRoute allowed={['agilebrain-admin']} data-id="element-52">
                          <ManageExercises data-id="element-53" />
                        </ProtectedRoute>} data-id="element-51" />
                    <Route path="/runner-preview" element={<RunnerPreview data-id="element-55" />} data-id="element-54" />
                    <Route path="/results" element={<Results data-id="element-57" />} data-id="element-56" />
                    <Route path="/integrations" element={<Integrations data-id="element-59" />} data-id="element-58" />
                    <Route path="/analytics" element={<ProtectedRoute allowed={['agilebrain-admin', 'client-admin']} data-id="element-61">
                          <Analytics data-id="element-62" />
                        </ProtectedRoute>} data-id="element-60" />
                    {/* Verification Test Route - Development Only */}
                    {process.env.NODE_ENV === 'development' && <Route path="/verification-test" element={<VerificationTest data-id="element-64" />} data-id="element-63" />}
                  </Routes>
                </ErrorBoundary>
              </Suspense>
            </div>
            <ErrorBoundary data-id="element-65">
              <MemoizedFooter data-id="element-66" />
            </ErrorBoundary>
          </div>
        </main>
      </div>
      <KeyboardShortcutsModal isOpen={showKeyboardShortcuts} onClose={() => setShowKeyboardShortcuts(false)} data-id="element-67" />
      <ErrorBoundary data-id="element-68">
        <ChatbotWrapper data-id="element-69" />
      </ErrorBoundary>
    </div>;
}
export function App() {
  return <ErrorBoundary data-id="element-70">
      <BrowserRouter data-id="element-71">
        <ScrollToTop data-id="element-72" />
        <ThemeProvider data-id="element-73">
          <AuthProvider data-id="element-74">
            <OrganizationProvider data-id="element-75">
              <ToastProvider data-id="element-76">
                <AppContent data-id="element-77" />
              </ToastProvider>
            </OrganizationProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>;
}